import { ClockCircleOutlined, CloseOutlined, TeamOutlined } from '@ant-design/icons';
import { Button, Input, Tag, Tooltip, TreeSelect, message } from 'antd';
import type { DataNode } from 'antd/es/tree';
import dayjs, { type Dayjs } from 'dayjs';
import { useRouter } from 'next/router';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { HEADER_ROUTES, NOT_ALLOW_EMPLOYEE_ROUTES, shouldHideEmployeeInTreeHeader } from '@/configs/ROUTE_HEADER';
import { getControllerFromRoute } from '@/helper/RouteHelper';
import { coCauGiaiDoanApi } from '@/services/organization/co-cau-giai-doan-api';
import { usePrivateStore } from '@/store/usePrivateStore';
import type { BoPhanTaiNgayView, MocLichSuView, NhanVienTaiNgayView } from '@/types/coCauGiaiDoan';
import { ChevronDown, ChevronRight, SearchIco, SelectIco } from '@/components/Icons/IconMenu';

type AnyRecord = Record<string, unknown>;

const unwrapData = <T,>(res: unknown): T | null => {
  const outer = res as { data?: unknown } | undefined;
  const data = outer?.data as { data?: T } | T | undefined;
  if (data && typeof data === 'object' && 'data' in (data as AnyRecord)) {
    return (data as { data?: T }).data ?? null;
  }
  return (data as T) ?? null;
};

const readString = (obj: AnyRecord | null | undefined, keys: string[], fallback = ''): string => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== undefined && value !== null) return String(value);
  }
  return fallback;
};

const readBool = (obj: AnyRecord | null | undefined, keys: string[], fallback = false): boolean => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== undefined && value !== null) return Boolean(value);
  }
  return fallback;
};

const mocKey = (moc: MocLichSuView): string =>
  String(moc.changeId || moc.snapshotAtCuoi || moc.ngayMoc || '').trim();

const mocDate = (moc: MocLichSuView): Dayjs => dayjs(moc.snapshotAtCuoi || moc.ngayMoc);

const formatMocLabel = (moc: MocLichSuView): string => {
  const d = mocDate(moc);
  const note = String(moc.ghiChu || '').trim();
  return `${d.isValid() ? d.format('DD/MM/YYYY') : 'Mốc cơ cấu'}${note ? ` - ${note}` : ''}`;
};

const deptId = (item: BoPhanTaiNgayView): string =>
  readString(item as unknown as AnyRecord, ['id', 'ID']);

const deptName = (item: BoPhanTaiNgayView): string =>
  readString(item as unknown as AnyRecord, ['ten', 'Ten'], deptId(item));

const deptParentId = (item: BoPhanTaiNgayView): string | null => {
  const value = readString(item as unknown as AnyRecord, ['parentID', 'ParentID'], '');
  return value && value !== '0' ? value : null;
};

const employeeId = (item: NhanVienTaiNgayView): string =>
  readString(item as unknown as AnyRecord, ['idNhanVien', 'IDNhanVien']);

const employeeName = (item: NhanVienTaiNgayView): string =>
  readString(item as unknown as AnyRecord, ['tenNhanVien', 'TenNhanVien'], employeeId(item));

const employeeDeptId = (item: NhanVienTaiNgayView): string =>
  readString(item as unknown as AnyRecord, ['bpid', 'BPID']);

const employeeIsQuanLy = (item: NhanVienTaiNgayView): boolean =>
  readBool(item as unknown as AnyRecord, ['isQuanLy', 'IsQuanLy']);

type HistoryOrgOption = {
  rawId: string;
  value: string;
  label: string;
  parentId: string | null;
  isEmployee: boolean;
  isCompany: boolean;
};

type ChangeBadge = {
  label: string;
  color: string;
};

type HistoryTreeNode = DataNode & {
  titleText?: string;
  /** Có NV (đã lọc trưởng phòng) — dùng để hiện icon Team như TreeSelect tổ chức. */
  hasEmployees?: boolean;
};

const normalizeSearchText = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'd')
    .toLowerCase();

const filterByTitleText = (input: string, node: { titleText?: unknown }): boolean => {
  const titleText = String(node?.titleText ?? '');
  return normalizeSearchText(titleText).includes(normalizeSearchText(input));
};

const getChangeBadge = (
  item: BoPhanTaiNgayView | NhanVienTaiNgayView,
  selectedChangeId?: string | null
): ChangeBadge | null => {
  const rec = item as unknown as AnyRecord;
  const itemChangeId = readString(rec, ['changeId', 'ChangeId'], '');
  if (!selectedChangeId || itemChangeId !== selectedChangeId) return null;

  const noteRaw = readString(rec, ['ghiChu', 'GhiChu'], '');
  const note = noteRaw.replace(/^đồng bộ\s*[·•\-–—:]?\s*/i, '').toLowerCase();
  // BE dùng cả "Tạo mới:" (CRUD BP) và "Thêm mới:" (TaiCoCau / NV); sync có prefix "Đồng bộ -"
  if (
    note.includes('tạo mới') ||
    note.includes('tao moi') ||
    note.includes('thêm mới') ||
    note.includes('them moi')
  ) {
    return { label: 'Mới', color: 'blue' };
  }
  if (note.includes('đổi tên') || note.includes('doi ten')) {
    // Snapshot cũ từng ghi luôn Parent với cùng GhiChu "Đổi tên: A → B".
    // Chỉ gắn tag khi tên node khớp tên mới (sau "→"); không khớp thì bỏ qua.
    const arrowMatch = noteRaw.match(/→\s*(.+)$/u) || noteRaw.match(/->\s*(.+)$/u);
    if (arrowMatch) {
      const newName = arrowMatch[1].trim().toLowerCase();
      const currentName = readString(
        rec,
        ['ten', 'Ten', 'tenNhanVien', 'TenNhanVien'],
        ''
      )
        .trim()
        .toLowerCase();
      if (currentName && newName && currentName !== newName) return null;
    }
    return { label: 'Đổi tên', color: 'purple' };
  }
  if (note.includes('chuyển') || note.includes('chuyen')) return { label: 'Chuyển', color: 'cyan' };
  if (note.includes('xóa') || note.includes('xoá') || note.includes('giai thể') || note.includes('giai the')) {
    return { label: 'Xóa', color: 'red' };
  }
  if (note.includes('quyền') || note.includes('quyen')) return { label: 'Đổi quyền', color: 'gold' };
  return { label: 'Thay đổi', color: 'orange' };
};

const renderNodeTitle = (
  label: string,
  badge?: ChangeBadge | null,
  extra?: React.ReactNode
): React.ReactNode => (
  <span className="inline-flex min-w-0 items-center gap-2">
    {extra}
    <span className="truncate">{label}</span>
    {badge ? <Tag color={badge.color} className="m-0">{badge.label}</Tag> : null}
  </span>
);

const renderDepartmentTitle = (
  label: string,
  badge: ChangeBadge | null
): React.ReactNode => (
  <span className="inline-flex min-w-0 items-center gap-2">
    <span className="truncate">{label}</span>
    {badge ? <Tag color={badge.color} className="m-0">{badge.label}</Tag> : null}
  </span>
);

const buildHistoryOrgTree = (
  departments: BoPhanTaiNgayView[],
  employees: NhanVienTaiNgayView[],
  selectedChangeId: string | null | undefined,
  employeeExpandedKeys: Set<string>,
  showEmployeesForSearch: boolean,
  showEmployees: boolean
): { treeData: HistoryTreeNode[]; optionMap: Map<string, HistoryOrgOption> } => {
  const activeDepartments = departments.filter((item) => !readBool(item as unknown as AnyRecord, ['isDelete', 'IsDelete']));
  const deptIds = new Set(activeDepartments.map(deptId).filter(Boolean));
  const byParent = new Map<string, BoPhanTaiNgayView[]>();
  const employeesByDept = new Map<string, NhanVienTaiNgayView[]>();
  const optionMap = new Map<string, HistoryOrgOption>();

  activeDepartments.forEach((item) => {
    const parent = deptParentId(item);
    const parentKey = parent && deptIds.has(parent) ? parent : '0';
    const list = byParent.get(parentKey) ?? [];
    list.push(item);
    byParent.set(parentKey, list);
  });

  if (showEmployees) {
    employees
      .filter((item) => !employeeIsQuanLy(item))
      .forEach((item) => {
        const bpid = employeeDeptId(item);
        if (!bpid) return;
        const list = employeesByDept.get(bpid) ?? [];
        list.push(item);
        employeesByDept.set(bpid, list);
      });
  }

  const build = (parentKey: string): HistoryTreeNode[] =>
    (byParent.get(parentKey) ?? [])
      .slice()
      .sort((a, b) => {
        const av = Number((a as unknown as AnyRecord).viTri ?? (a as unknown as AnyRecord).ViTri ?? 0);
        const bv = Number((b as unknown as AnyRecord).viTri ?? (b as unknown as AnyRecord).ViTri ?? 0);
        if (av !== bv) return av - bv;
        return deptName(a).localeCompare(deptName(b), 'vi');
      })
      .map((item) => {
        const id = deptId(item);
        const parentId = deptParentId(item);
        const value = `dept:${id}`;
        const label = deptName(item);
        optionMap.set(value, {
          rawId: id,
          value,
          label,
          parentId,
          isEmployee: false,
          isCompany: !parentId,
        });

        const employeeOpen = showEmployees && (employeeExpandedKeys.has(id) || showEmployeesForSearch);
        const employeeNodes: HistoryTreeNode[] = employeeOpen
          ? (employeesByDept.get(id) ?? [])
              .slice()
              .sort((a, b) => employeeName(a).localeCompare(employeeName(b), 'vi'))
              .map((employee) => {
                const rawId = employeeId(employee);
                const employeeValue = `emp:${rawId}`;
                const empLabel = employeeName(employee);
                const badge = getChangeBadge(employee, selectedChangeId);
                optionMap.set(employeeValue, {
                  rawId,
                  value: employeeValue,
                  label: empLabel,
                  parentId: id,
                  isEmployee: true,
                  isCompany: false,
                });
                return {
                  key: employeeValue,
                  value: employeeValue,
                  titleText: empLabel,
                  // Khớp tổ chức: không tag "NV"; chữ xanh qua .employee-node (titleRender).
                  title: renderNodeTitle(empLabel, badge),
                };
              })
          : [];
        const hasEmployees = showEmployees && (employeesByDept.get(id) ?? []).length > 0;
        const childDepartments = build(id);

        return {
          key: value,
          value,
          titleText: label,
          hasEmployees,
          title: renderDepartmentTitle(label, getChangeBadge(item, selectedChangeId)),
          // Khớp TreeSelect tổ chức: NV đứng trước BP con dưới cùng cha.
          children: [...employeeNodes, ...childDepartments],
        };
      });

  return { treeData: build('0'), optionMap };
};

type Props = {
  year: number;
};

export const CoCauHistorySelector: React.FC<Props> = ({ year }) => {
  const router = useRouter();
  const { coCauHistory, setCoCauHistory, clearCoCauHistory } = usePrivateStore();
  const [loadingMocs, setLoadingMocs] = useState(false);
  const [loadingTree, setLoadingTree] = useState(false);
  const [mocs, setMocs] = useState<MocLichSuView[]>([]);
  const [selectedMocKey, setSelectedMocKey] = useState<string | undefined>(
    coCauHistory.changeId || coCauHistory.idCoCauGiaiDoan || undefined
  );
  const [departmentsAtMoc, setDepartmentsAtMoc] = useState<BoPhanTaiNgayView[]>([]);
  const [employeesAtMoc, setEmployeesAtMoc] = useState<NhanVienTaiNgayView[]>([]);
  const [orgExpandedKeys, setOrgExpandedKeys] = useState<string[]>([]);
  const [employeeExpandedKeys, setEmployeeExpandedKeys] = useState<string[]>([]);
  const [orgSearchTerm, setOrgSearchTerm] = useState('');

  // Khớp TreeSelect tổ chức (DropDownDepartment): theo route có được chọn NV hay không.
  const controller = useMemo(() => {
    const pathname = (router.asPath || router.pathname || '').split('?')[0];
    return getControllerFromRoute(pathname) ?? '';
  }, [router.asPath, router.pathname]);

  const isShowEmployee = useMemo(() => {
    const isCompanyOnlyRoute = HEADER_ROUTES.CONGTY_ROUTES.includes(controller);
    const hideEmployeeForApv2Manager = shouldHideEmployeeInTreeHeader(
      router.pathname,
      router.query as Record<string, string | string[] | undefined>
    );
    return (
      !isCompanyOnlyRoute &&
      !NOT_ALLOW_EMPLOYEE_ROUTES.includes(controller) &&
      !hideEmployeeForApv2Manager
    );
  }, [controller, router.pathname, router.query]);

  useEffect(() => {
    if (coCauHistory.enabled && coCauHistory.nam && coCauHistory.nam !== year) {
      clearCoCauHistory();
      setSelectedMocKey(undefined);
    }
  }, [clearCoCauHistory, coCauHistory.enabled, coCauHistory.nam, year]);

  // Trang chỉ chọn BP: nếu đang chọn NV thì chuyển về BP cha (giống tổ chức).
  useEffect(() => {
    if (isShowEmployee || !coCauHistory.enabled || !coCauHistory.isEmployee) return;
    const parentDeptId = coCauHistory.selectedDepartmentParentId;
    if (!parentDeptId) return;

    const dept = departmentsAtMoc.find((item) => deptId(item) === parentDeptId);
    const parentId = dept ? deptParentId(dept) : null;
    const label = (dept ? deptName(dept) : '') || coCauHistory.selectedDepartmentName || parentDeptId;

    setCoCauHistory({
      selectedDepartmentId: parentDeptId,
      selectedDepartmentName: label,
      selectedDepartmentParentId: parentId,
      isEmployee: false,
      isCompany: !parentId,
    });
  }, [
    coCauHistory.enabled,
    coCauHistory.isEmployee,
    coCauHistory.selectedDepartmentName,
    coCauHistory.selectedDepartmentParentId,
    departmentsAtMoc,
    isShowEmployee,
    setCoCauHistory,
  ]);

  useEffect(() => {
    let mounted = true;
    const loadMocs = async () => {
      setLoadingMocs(true);
      try {
        const res = await coCauGiaiDoanApi.getMocList(
          dayjs(`${year}-01-01`).format('YYYY-MM-DD'),
          dayjs(`${year}-12-31`).format('YYYY-MM-DD')
        );
        const list = unwrapData<MocLichSuView[]>(res) ?? [];
        if (mounted) setMocs(list.filter((item) => mocKey(item)));
      } catch {
        if (mounted) {
          setMocs([]);
          message.error('Không thể tải mốc cơ cấu');
        }
      } finally {
        if (mounted) setLoadingMocs(false);
      }
    };

    void loadMocs();
    return () => {
      mounted = false;
    };
  }, [year]);

  const selectedMoc = useMemo(
    () => mocs.find((item) => mocKey(item) === selectedMocKey),
    [mocs, selectedMocKey]
  );

  useEffect(() => {
    if (!selectedMoc) {
      setDepartmentsAtMoc([]);
      setEmployeesAtMoc([]);
      setOrgExpandedKeys([]);
      setEmployeeExpandedKeys([]);
      return;
    }

    let mounted = true;
    const loadTree = async () => {
      setLoadingTree(true);
      try {
        const snapshotAt = selectedMoc.snapshotAtCuoi || selectedMoc.ngayMoc;
        const changeId = selectedMoc.changeId || undefined;
        const deptRes = await coCauGiaiDoanApi.getOrgTreeAtDate(snapshotAt, changeId);
        if (!mounted) return;
        setDepartmentsAtMoc(unwrapData<BoPhanTaiNgayView[]>(deptRes) ?? []);

        if (isShowEmployee) {
          const empRes = await coCauGiaiDoanApi.getNhanVienTaiNgay(snapshotAt, changeId);
          if (!mounted) return;
          setEmployeesAtMoc(unwrapData<NhanVienTaiNgayView[]>(empRes) ?? []);
        } else {
          setEmployeesAtMoc([]);
        }
      } catch {
        if (mounted) {
          setDepartmentsAtMoc([]);
          setEmployeesAtMoc([]);
          message.error('Không thể tải cây cơ cấu tại mốc');
        }
      } finally {
        if (mounted) setLoadingTree(false);
      }
    };

    void loadTree();
    return () => {
      mounted = false;
    };
  }, [isShowEmployee, selectedMoc]);

  const mocTreeData = useMemo<DataNode[]>(() => {
    const monthMap = new Map<string, MocLichSuView[]>();
    mocs.forEach((item) => {
      const d = mocDate(item);
      const monthKey = d.isValid() ? d.format('YYYY-MM') : 'unknown';
      const list = monthMap.get(monthKey) ?? [];
      list.push(item);
      monthMap.set(monthKey, list);
    });

    const monthNodes: DataNode[] = Array.from(monthMap.entries())
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([monthKey, list]) => {
        const d = dayjs(`${monthKey}-01`);
        const sorted = list.slice().sort((a, b) => mocDate(b).valueOf() - mocDate(a).valueOf());
        return {
          key: `month:${monthKey}`,
          value: `month:${monthKey}`,
          selectable: false,
          titleText: d.isValid() ? `Tháng ${d.format('MM/YYYY')}` : 'Mốc khác',
          title: (
            <span className="inline-flex w-full items-center justify-between gap-3">
              <span>{d.isValid() ? `Tháng ${d.format('MM/YYYY')}` : 'Mốc khác'}</span>
              <Tag className="m-0">{sorted.length} mốc</Tag>
            </span>
          ),
          children: sorted.map((item) => ({
            key: mocKey(item),
            value: mocKey(item),
            titleText: formatMocLabel(item),
            title: formatMocLabel(item),
          })),
        };
      });

    return [
      {
        key: 'live',
        value: 'live',
        titleText: 'Hiện tại',
        title: 'Hiện tại',
      },
      ...monthNodes,
    ];
  }, [mocs]);

  const selectedChangeId = selectedMoc ? selectedMoc.changeId || mocKey(selectedMoc) : null;

  const selectedOrgValue = useMemo(() => {
    if (!coCauHistory.enabled || !coCauHistory.selectedDepartmentId) return undefined;
    return `${coCauHistory.isEmployee ? 'emp' : 'dept'}:${coCauHistory.selectedDepartmentId}`;
  }, [coCauHistory.enabled, coCauHistory.isEmployee, coCauHistory.selectedDepartmentId]);

  const toggleEmployeeGroup = useCallback((deptId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setEmployeeExpandedKeys((prev) =>
      prev.includes(deptId) ? prev.filter((key) => key !== deptId) : [...prev, deptId]
    );
  }, []);

  useEffect(() => {
    if (selectedOrgValue?.startsWith('emp:')) {
      const deptId = coCauHistory.selectedDepartmentParentId;
      if (deptId) {
        setEmployeeExpandedKeys((prev) => (prev.includes(deptId) ? prev : [...prev, deptId]));
      }
    }
  }, [coCauHistory.selectedDepartmentParentId, selectedOrgValue]);

  const { treeData: orgTreeData, optionMap } = useMemo(
    () => buildHistoryOrgTree(
      departmentsAtMoc,
      employeesAtMoc,
      selectedChangeId,
      new Set(employeeExpandedKeys),
      Boolean(orgSearchTerm.trim()),
      isShowEmployee
    ),
    [
      departmentsAtMoc,
      employeeExpandedKeys,
      employeesAtMoc,
      isShowEmployee,
      orgSearchTerm,
      selectedChangeId,
    ]
  );

  const filteredOrgTreeData = useMemo(() => {
    const keyword = normalizeSearchText(orgSearchTerm.trim());
    if (!keyword) return orgTreeData;

    const filterNodes = (nodes: HistoryTreeNode[]): HistoryTreeNode[] =>
      nodes.flatMap((node) => {
        const children = filterNodes((node.children ?? []) as HistoryTreeNode[]);
        const matched = normalizeSearchText(String(node.titleText ?? '')).includes(keyword);
        return matched || children.length > 0 ? [{ ...node, children }] : [];
      });

    return filterNodes(orgTreeData);
  }, [orgSearchTerm, orgTreeData]);

  const searchExpandedKeys = useMemo(() => {
    if (!orgSearchTerm.trim()) return undefined;
    const keys: string[] = [];
    const collect = (nodes: HistoryTreeNode[]) => {
      nodes.forEach((node) => {
        keys.push(String(node.key));
        collect((node.children ?? []) as HistoryTreeNode[]);
      });
    };
    collect(filteredOrgTreeData);
    return keys;
  }, [filteredOrgTreeData, orgSearchTerm]);

  useEffect(() => {
    const departmentKeys: string[] = [];
    const collect = (nodes: HistoryTreeNode[]) => {
      nodes.forEach((node) => {
        const key = String(node.key);
        if (key.startsWith('dept:')) departmentKeys.push(key);
        collect((node.children ?? []) as HistoryTreeNode[]);
      });
    };
    collect(orgTreeData);
    setOrgExpandedKeys(departmentKeys);
  }, [selectedMocKey, orgTreeData]);

  const effectiveOrgExpandedKeys = searchExpandedKeys ?? orgExpandedKeys;

  const renderHistoryOrgTitle = useCallback(
    (node: HistoryTreeNode) => {
      const key = String(node.key);
      const isEmployee = key.startsWith('emp:');
      if (isEmployee) {
        // Khớp TreeSelect tổ chức: tên xanh, không tag NV.
        return (
          <span className="employee-node" style={{ color: '#0C68E9', fontWeight: 500 }}>
            {node.title as React.ReactNode}
          </span>
        );
      }

      const deptIdFromKey = key.startsWith('dept:') ? key.slice('dept:'.length) : '';
      const hasEmployees = Boolean(node.hasEmployees);
      const employeeGroupOpen =
        (Boolean(orgSearchTerm.trim()) && hasEmployees) ||
        (!orgSearchTerm.trim() && Boolean(deptIdFromKey) && employeeExpandedKeys.includes(deptIdFromKey));
      const hasChildren = Boolean(node.children?.length) || hasEmployees;
      const isExpanded = effectiveOrgExpandedKeys.includes(key);

      return (
        <div className="flex w-full min-w-0 items-center">
          <span className="min-w-0 flex-1">{node.title as React.ReactNode}</span>
          {hasEmployees ? (
            <Button
              type="text"
              size="small"
              className="!ml-1 !h-5 !min-w-0 !flex-shrink-0"
              icon={
                <TeamOutlined
                  style={{ fontSize: 12, color: employeeGroupOpen ? '#0C68E9' : '#919eab' }}
                />
              }
              onClick={(event) => {
                if (deptIdFromKey) toggleEmployeeGroup(deptIdFromKey, event);
              }}
            />
          ) : null}
          {hasChildren ? (
            <span
              className="custom-icon-expand ml-1 flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg hover:bg-[rgba(0,0,0,0.08)]"
              onClick={(event) => {
                event.stopPropagation();
                setOrgExpandedKeys((previous) =>
                  previous.includes(key)
                    ? previous.filter((item) => item !== key)
                    : [...previous, key]
                );
              }}
            >
              {isExpanded ? <ChevronDown /> : <ChevronRight />}
            </span>
          ) : null}
        </div>
      );
    },
    [effectiveOrgExpandedKeys, employeeExpandedKeys, orgSearchTerm, toggleEmployeeGroup]
  );

  const handleMocChange = (value?: string) => {
    if (!value || value === 'live') {
      setSelectedMocKey(undefined);
      clearCoCauHistory();
      return;
    }
    setSelectedMocKey(value);
    setEmployeeExpandedKeys([]);
    setOrgSearchTerm('');
    clearCoCauHistory();
  };

  const handleOrgChange = (value?: string) => {
    if (!selectedMoc || !value) return;
    const option = optionMap.get(value);
    if (!option) return;
    if (option.isEmployee && !isShowEmployee) return;

    const snapshotAt = selectedMoc.snapshotAtCuoi || selectedMoc.ngayMoc;
    const d = dayjs(snapshotAt);
    const month = d.isValid() ? d.month() + 1 : 1;
    const historyYear = d.isValid() ? d.year() : year;
    const changeId = selectedMoc.changeId || mocKey(selectedMoc);

    setCoCauHistory({
      enabled: true,
      idCoCauGiaiDoan: changeId,
      changeId,
      snapshotAt,
      nam: historyYear,
      label: formatMocLabel(selectedMoc),
      tuThang: month,
      denThang: month,
      selectedDepartmentId: option.rawId,
      selectedDepartmentName: option.label,
      selectedDepartmentParentId: option.parentId ?? '0',
      isEmployee: option.isEmployee,
      isCompany: option.isCompany,
    });
    setOrgSearchTerm('');
  };

  // Mặc định ẩn; chỉ hiện khi đã load xong và có giai đoạn lịch sử
  if (loadingMocs || mocs.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-x-2">
      <span className="whitespace-nowrap text-[#1c252e] text-sm font-semibold ml-2">
        Cơ cấu giai đoạn:
      </span>
      <TreeSelect
        showSearch
        className="co-cau-history-tree-select"
        classNames={{ popup: { root: 'co-cau-history-tree-select-dropdown' } }}
        dropdownMatchSelectWidth={false}
        variant="borderless"
        suffixIcon={<SelectIco />}
        placeholder="Mốc cơ cấu"
        loading={loadingMocs}
        value={selectedMocKey ?? 'live'}
        treeData={mocTreeData}
        treeNodeLabelProp="titleText"
        filterTreeNode={(input, node) => filterByTitleText(input, node as { titleText?: unknown })}
        onChange={handleMocChange}
      />
      {selectedMoc ? (
        <TreeSelect
          className="custom-tree-select co-cau-history-tree-select"
          classNames={{ popup: { root: 'custom-tree-select-dropdown co-cau-history-tree-select-dropdown' } }}
          dropdownMatchSelectWidth={false}
          variant="borderless"
          suffixIcon={<SelectIco />}
          placeholder="Đối tượng tại mốc"
          loading={loadingTree}
          value={selectedOrgValue}
          treeData={filteredOrgTreeData}
          treeNodeLabelProp="titleText"
          treeExpandedKeys={effectiveOrgExpandedKeys}
          onTreeExpand={(keys) => setOrgExpandedKeys(keys.map(String))}
          treeLine={{ showLeafIcon: false }}
          switcherIcon={() => null}
          treeTitleRender={(node) => renderHistoryOrgTitle(node as HistoryTreeNode)}
          popupRender={(menu) => (
            <div className="custom-tree-select-dropdown-wrapper">
              <Input
                size="large"
                allowClear
                placeholder="Tìm kiếm"
                value={orgSearchTerm}
                onChange={(event) => setOrgSearchTerm(event.target.value)}
                onClick={(event) => event.stopPropagation()}
                className="mb-4 !text-sm"
                prefix={<SearchIco />}
              />
              {menu}
            </div>
          )}
          filterTreeNode={false}
          onSearch={setOrgSearchTerm}
          onChange={handleOrgChange}
        />
      ) : null}
      {coCauHistory.enabled ? (
        <Tooltip title="Thoát lịch sử cơ cấu">
          <Button
            className="co-cau-history-exit-button"
            type="text"
            icon={<CloseOutlined />}
            onClick={() => {
              clearCoCauHistory();
              setSelectedMocKey(undefined);
              setEmployeeExpandedKeys([]);
              setOrgSearchTerm('');
            }}
          />
        </Tooltip>
      ) : (
        <Tooltip title="Chọn mốc cơ cấu để xem dữ liệu lịch sử">
          <Button className="co-cau-history-exit-button" type="text" icon={<ClockCircleOutlined />} disabled />
        </Tooltip>
      )}
      <style>{`
        .co-cau-history-tree-select {
          height: 26px !important;
        }
        .co-cau-history-tree-select .ant-select-selector {
          height: 26px !important;
          min-height: 26px !important;
          display: flex !important;
          align-items: center !important;
          background-color: #919eab29 !important;
          border-radius: 6px !important;
          padding-inline: 8px 25px !important;
          font-size: 12px !important;
          font-weight: 700 !important;
        }
        .co-cau-history-tree-select .ant-select-selection-item,
        .co-cau-history-tree-select .ant-select-selection-placeholder {
          padding-inline-end: 0 !important;
          font-size: 12px !important;
          font-weight: 700 !important;
          line-height: 26px !important;
        }
        .co-cau-history-tree-select .ant-select-arrow {
          inset-inline-end: 8px !important;
          color: #919eab;
        }
        .co-cau-history-exit-button {
          width: 22px !important;
          min-width: 22px !important;
          height: 24px !important;
          padding: 0 !important;
          border-radius: 6px !important;
          background-color: #919eab29 !important;
          color: #919eab !important;
        }
        .co-cau-history-tree-select-dropdown {
          font-size: 12px;
        }
        .co-cau-history-tree-select-dropdown .ant-select-tree-node-selected {
          background-color: #0c68e9 !important;
          color: #fff !important;
        }
        /* Tag solid trên dòng selected: chữ trắng trên nền màu tag (đọc rõ trên highlight xanh) */
        .co-cau-history-tree-select-dropdown .ant-select-tree-node-selected .ant-tag {
          border-color: transparent !important;
          font-weight: 600 !important;
        }
        .co-cau-history-tree-select-dropdown .ant-select-tree-node-selected .ant-tag-orange {
          color: #fff !important;
          background: #fa8c16 !important;
        }
        .co-cau-history-tree-select-dropdown .ant-select-tree-node-selected .ant-tag-blue {
          color: #fff !important;
          background: #1677ff !important;
          box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.45);
        }
        .co-cau-history-tree-select-dropdown .ant-select-tree-node-selected .ant-tag-purple {
          color: #fff !important;
          background: #722ed1 !important;
        }
        .co-cau-history-tree-select-dropdown .ant-select-tree-node-selected .ant-tag-cyan {
          color: #fff !important;
          background: #13c2c2 !important;
        }
        .co-cau-history-tree-select-dropdown .ant-select-tree-node-selected .ant-tag-red {
          color: #fff !important;
          background: #f5222d !important;
        }
        .co-cau-history-tree-select-dropdown .ant-select-tree-node-selected .ant-tag-gold {
          color: #614700 !important;
          background: #ffc53d !important;
        }
        .co-cau-history-tree-select-dropdown .ant-select-tree-node-selected .anticon {
          color: #fff !important;
        }
        /* NV node: chữ xanh như tổ chức; trắng khi selected */
        .co-cau-history-tree-select-dropdown .employee-node {
          color: #0c68e9;
          font-weight: 500;
        }
        .co-cau-history-tree-select-dropdown .ant-select-tree-node-selected .employee-node {
          color: #fff !important;
        }
        .co-cau-history-tree-select-dropdown .ant-select-tree-checkbox {
          display: none !important;
        }
      `}</style>
    </div>
  );
};
